import { Color, ScaleType } from '@swimlane/ngx-charts';

/**
 * Shared color definitions for analytics charts
 *
 * SEMANTIC COLOR SYSTEM:
 * - Blue (#3b82f6): Jobs (all job-related metrics)
 * - Purple (#8b5cf6): Candidates (all candidate-related metrics)
 * - Green (#10b981): Success metrics (hired, created)
 * - Amber (#f59e0b): Activity metrics (applications, registrations)
 * - Pink (#ec4899): Ratios and rates (calculated metrics)
 */

// =====================================================
// SHARED METRIC COLORS (for CSS classes)
// =====================================================
export const METRIC_COLORS = {
  // Primary entity colors
  jobs: '#3b82f6',           // Blue
  candidates: '#8b5cf6',     // Purple

  // Activity colors
  applications: '#f59e0b',   // Amber

  // Success colors
  hired: '#10b981',          // Green
  created: '#10b981',        // Green

  // Calculated metrics
  rate: '#ec4899',           // Pink
  ratio: '#ec4899',          // Pink

  // Time metrics
  days: '#3b82f6'            // Blue
} as const;

// =====================================================
// HIRING RATE COLORS
// =====================================================
export const HIRING_RATE_COLORS = {
  hired: '#10b981',        // Green - success metric
  applications: '#f59e0b', // Amber - activity metric
  rate: '#ec4899'          // Pink - ratio metric
} as const;

export const HIRING_RATE_COLOR_SCHEME: Color = {
  name: 'hiringRateScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [
    HIRING_RATE_COLORS.hired,
    HIRING_RATE_COLORS.applications,
    HIRING_RATE_COLORS.rate
  ]
};

// =====================================================
// APPLICATIONS COLORS
// =====================================================
export const APPLICATIONS_COLORS = {
  totalApplications: '#f59e0b',  // Amber - activity metric (consistent with METRIC_COLORS.applications)
  uniqueCandidates: '#8b5cf6',   // Purple - candidate metric
  uniqueJobs: '#3b82f6'          // Blue - job metric
} as const;

export const APPLICATIONS_COLOR_SCHEME: Color = {
  name: 'applicationsScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [
    APPLICATIONS_COLORS.totalApplications,
    APPLICATIONS_COLORS.uniqueCandidates,
    APPLICATIONS_COLORS.uniqueJobs
  ]
};

// =====================================================
// AVAILABLE JOBS COLORS
// =====================================================
export const AVAILABLE_JOBS_COLORS = {
  jobs: '#3b82f6'  // Blue
} as const;

export const AVAILABLE_JOBS_COLOR_SCHEME: Color = {
  name: 'availableJobsScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [AVAILABLE_JOBS_COLORS.jobs]
};

// =====================================================
// AVAILABLE CANDIDATES COLORS
// =====================================================
export const AVAILABLE_CANDIDATES_COLORS = {
  candidates: '#8b5cf6'  // Purple
} as const;

export const AVAILABLE_CANDIDATES_COLOR_SCHEME: Color = {
  name: 'availableCandidatesScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [AVAILABLE_CANDIDATES_COLORS.candidates]
};

// =====================================================
// CREATED JOBS COLORS
// =====================================================
export const CREATED_JOBS_COLORS = {
  jobs: '#10b981'  // Green
} as const;

export const CREATED_JOBS_COLOR_SCHEME: Color = {
  name: 'createdJobsScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [CREATED_JOBS_COLORS.jobs]
};

// =====================================================
// REGISTERED CANDIDATES COLORS
// =====================================================
export const REGISTERED_CANDIDATES_COLORS = {
  candidates: '#f59e0b'  // Amber
} as const;

export const REGISTERED_CANDIDATES_COLOR_SCHEME: Color = {
  name: 'registeredCandidatesScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [REGISTERED_CANDIDATES_COLORS.candidates]
};

// =====================================================
// SUPPLY DEMAND COLORS
// =====================================================
export const SUPPLY_DEMAND_COLORS = {
  jobs: '#3b82f6',        // Blue - job metric
  candidates: '#8b5cf6',  // Purple - candidate metric
  ratio: '#ec4899'        // Pink - ratio metric
} as const;

export const SUPPLY_DEMAND_COLOR_SCHEME: Color = {
  name: 'supplyDemandScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: [
    SUPPLY_DEMAND_COLORS.jobs,
    SUPPLY_DEMAND_COLORS.candidates,
    SUPPLY_DEMAND_COLORS.ratio
  ]
};

// =====================================================
// CATEGORY/BREAKDOWN COLORS (for bar charts)
// =====================================================
export const CATEGORY_BREAKDOWN_COLOR_SCHEME: Color = {
  name: 'categoryScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5']  // Green shades
};

export const POSITION_BREAKDOWN_COLOR_SCHEME: Color = {
  name: 'positionScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7']  // Amber shades
};

export const LOCATION_BREAKDOWN_COLOR_SCHEME: Color = {
  name: 'locationScheme',
  selectable: true,
  group: ScaleType.Ordinal,
  domain: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe']  // Purple shades
};
